import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { useRacesList } from '../../races/contexts/races';
import { useGenealogy } from '../contexts/genealogy';

import './styles/tree.css';

// Árbol genealógico de una vaca (admin-genealogy.md), montado en el detalle del animal.
// Layout descendente y compacto (ul/li → conectores CSS): el animal arriba, padre/madre
// (+ abuelos) debajo. El nodo trae solo `race_id` → se mapea al nombre con la lista de
// razas. `ref:true` = nodo ya mostrado (consanguinidad) → no se re-expande. Solo lectura:
// el backend calcula/acota el árbol; el front lo muestra.
const SEX_LABEL = { female: 'Hembra', male: 'Macho' };
const DEPTH = 2; // solo 2 generaciones (padres + abuelos)

const sexClass = (sex) => (sex === 'male' ? 'male' : sex === 'female' ? 'female' : 'unknown');

function GenealogyNode({ node, raceMap, isRoot = false, role = null }) {
    if (!node) return null;

    const isRef = node.ref === true;
    const hasParents = !isRef && (node.father || node.mother);
    const raceName = node.race_id != null ? raceMap.get(node.race_id) : null;
    const label = node.name || node.number || `#${node.id}`;

    return (
        <li className="genealogy-node">
            {role && <span className={`genealogy-node__role genealogy-node__role--${role === 'Padre' ? 'father' : 'mother'}`}>{role}</span>}

            <div className={`genealogy-card genealogy-card--${sexClass(node.sex)} ${isRoot ? 'genealogy-card--root' : ''} ${isRef ? 'genealogy-card--ref' : ''}`}>
                <div className="genealogy-card__head">
                    <strong className="genealogy-card__name" title={label}>{label}</strong>
                    {node.number && node.name && <span className="genealogy-card__number">{node.number}</span>}
                </div>
                <div className="genealogy-card__meta">
                    {raceName && <span className="genealogy-card__tag">{raceName}</span>}
                    {node.sex && <span className="genealogy-card__tag">{SEX_LABEL[node.sex] || node.sex}</span>}
                    {node.baptized === false && <span className="genealogy-card__tag genealogy-card__tag--warn">Sin bautizar</span>}
                </div>
                {isRef && <span className="genealogy-card__ref">↗ ya mostrado</span>}
                {!isRoot && node.id != null && (
                    <Link to={`/admin/animals/${node.id}`} className="genealogy-card__link">Ver ficha →</Link>
                )}
            </div>

            {hasParents && (
                <ul className="genealogy-node__parents">
                    {node.father
                        ? <GenealogyNode node={node.father} raceMap={raceMap} role="Padre" />
                        : <EmptyParent type="father" />}
                    {node.mother
                        ? <GenealogyNode node={node.mother} raceMap={raceMap} role="Madre" />
                        : <EmptyParent type="mother" />}
                </ul>
            )}
        </li>
    );
}

function EmptyParent({ type }) {
    const isFather = type === 'father';
    return (
        <li className="genealogy-node">
            <span className={`genealogy-node__role genealogy-node__role--${isFather ? 'father' : 'mother'}`}>{isFather ? 'Padre' : 'Madre'}</span>
            <div className={`genealogy-card genealogy-card--empty genealogy-card--${isFather ? 'male' : 'female'}`}>
                <span className="genealogy-card__placeholder">{isFather ? 'Sin padre' : 'Sin madre'}</span>
            </div>
        </li>
    );
}

export default function GenealogyTree({ animalId }) {
    const { data, isLoading, isFetching, error } = useGenealogy(animalId, DEPTH);

    const { data: racesData } = useRacesList({});
    const raceMap = useMemo(() => {
        const m = new Map();
        for (const r of racesData?.items ?? []) m.set(r.id, r.name);
        return m;
    }, [racesData]);

    const tree = data?.tree;
    const rootHasParents = !!(tree && (tree.father || tree.mother));

    return (
        <section className="cat-detail__card">
            <div className="genealogy__head">
                <h2 className="cat-detail__section-title">Genealogía</h2>
                <span className="genealogy__caption">2 generaciones</span>
            </div>

            {error ? (
                <p className="genealogy__state">
                    {error.status === 403 ? 'Sin permiso para ver la genealogía.' : (error.message || 'No se pudo cargar la genealogía.')}
                </p>
            ) : isLoading && !data ? (
                <p className="genealogy__state">Cargando genealogía…</p>
            ) : !tree ? (
                <p className="genealogy__state">Sin genealogía registrada.</p>
            ) : (
                <>
                    {!rootHasParents && (
                        <p className="genealogy__hint">Sin ancestros registrados. La genealogía de crías nacidas en el sistema la arma el parto; la de animales comprados se registra a mano.</p>
                    )}
                    <div className={`genealogy__tree-wrap ${isFetching ? 'genealogy__tree-wrap--busy' : ''}`}>
                        <ul className="genealogy__tree">
                            <GenealogyNode node={tree} raceMap={raceMap} isRoot />
                        </ul>
                    </div>
                </>
            )}
        </section>
    );
}
